import { createStackNavigator, StackNavigationProp } from '@react-navigation/stack';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { MenuProvider } from 'react-native-popup-menu';

import ChangeLogs from './pages/ChangeLogs';
import ContactUs from './pages/ContactUs';
import FrequentlyAskedQuestions from './pages/FrequentlyAskedQuestions';
import PublicFrequentlyAskedQuestions from './pages/PublicFrequentlyAskedQuestions';
import Login from './pages/Login';
import Menu from './pages/Menu';
import ResetPassword from './pages/ResetPassword';
import TermsOfUse from './pages/TermsOfUse';
import RotatePassword from './pages/RotatePassword';
import { i18nKeys, translate } from './services/translate';

import {
    faClipboardList,
    faBuildingShield,
    faCar,
    faFolderOpen,
    faTag,
    faUser,
    faMagnifyingGlass,
    faTriangleExclamation,
    faUserLock,
    faClipboard,
    faKey,
    faSitemap,
    faFileContract,
    faCloudArrowUp,
    faCloudArrowDown,
    faServer,
    faBan,
    faFilePen,
    faHexagonNodesBolt,
    faCodeCommit,
    faPhone,
    faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faCameraCctv,
    IconDefinition as ProIconDefinition,
    faSearch,
    faIdBadge,
    faFileSignature,
    faMemoCircleCheck,
    faCarBus,
    faClipboardListCheck,
    faClock,
    faHandHoldingBox,
    faChartPieSimple,
} from '@fortawesome/pro-solid-svg-icons';

// facial
import LiveAlertEventDetail from './pages/facial/LiveAlerts/LiveAlertEventDetail';
import LiveAlerts from './pages/facial/LiveAlerts/LiveAlerts';
import LiveEventDetail from './pages/facial/LiveEvents/LiveEventDetail';
import LiveEvents from './pages/facial/LiveEvents/LiveEvents';
import Markers from './pages/facial/Markers/Markers';
import IgnoredPeople from './pages/facial/IgnoredPeople/IgnoredPeople';
import PeopleList from './pages/facial/People/PeopleList';
import PersonForm from './pages/facial/PersonForm/PersonForm';
import Search from './pages/facial/Search/Search';
import SearchEventDetail from './pages/facial/Search/SearchEventDetail';

// lpr
import LiveAlertLprDetection from './pages/lpr/Detection/LiveAlertLprDetection';
import LiveEventLprDetection from './pages/lpr/Detection/LiveEventLprDetection';
import SearchLprDetection from './pages/lpr/Detection/SearchLprDetection';
import LprLiveAlerts from './pages/lpr/LiveAlerts/LiveAlerts';
import LprLiveEvents from './pages/lpr/LiveEvents/LiveEvents';
import Plates from './pages/lpr/Plates/Plates';
import LprReports from './pages/lpr/Reports/Reports';
import LprSearch from './pages/lpr/Search/Search';

// events
import EventsBoard from './pages/events/EventsBoard';
import EventsOccurrenceDetail from './pages/events/EventsOccurrenceDetail';

// Dashboard
import DashboardAgents from './pages/dashboard/DashboardAgents/DashboardAgents';
import DashboardCameras from './pages/dashboard/DashboardCameras';
import DashboardOccurrences from './pages/dashboard/DashboardOccurrences';
import DashboardVehicles from './pages/dashboard/DashboardVehicles/DashboardVehicles';
import DashboardPlateMonitoring from './pages/dashboard/DashboardPlateMonitoring/DashboardPlateMonitoring';

// Logs
import LogList from './pages/logs/LogList/LogList';
import LogReports from './pages/logs/Reports/Reports';

// Smart Search Facial
import SmartSearchFacial from './pages/smart-search-facial/SmartSearchFacial';

// Plate Validation
import CloneSuspicions from './pages/cloned-plate/clone-suspicions/CloneSuspicions';
import ClonedPlates from './pages/cloned-plate/cloned-plates/ClonedPlates';

// Signatures
import Signatures from './pages/signatures/Signatures';

// Armory
import ArmoryShifts from './pages/armory/ArmoryShifts/ArmoryShifts';
import ArmoryShiftForm from './pages/armory/ArmoryShifts/ArmoryShiftForm';
import MyArmoryShifts from './pages/armory/MyArmoryShifts/MyArmoryShifts';

// Deac
import VacancyLevels from './pages/deac/VacancyLevels/VacancyLevels';
import Workplaces from './pages/deac/Workplaces/Workplaces';
import VacancyTags from './pages/deac/VacancyTags/VacancyTags';
import VacancyCancellationReasons from './pages/deac/VacancyCancellationReasons/VacancyCancellationReasons';
import Vacancies from './pages/deac/Vacancies/Vacancies';
import VacancyForm from './pages/deac/Vacancies/VacancyForm';
import PresenceControl from './pages/deac/PresenceControl/PresenceControl';
import DeacReports from './pages/deac/Reports/Reports';
import Dashboard from './pages/deac/Dashboard/Dashboard';
import Applications from './pages/deac/Applications/Applications';
import VacanciesTemplates from './pages/deac/VacanciesTemplates/VacanciesTemplates';
import VacancyTemplateForm from './pages/deac/VacanciesTemplates/VacancyTemplateForm';

// Third Party Approved Companies
import Inscriptions from './pages/third-party-approved-companies/Inscriptions/Inscriptions';
import InscriptionForm from './pages/third-party-approved-companies/Inscriptions/InscriptionForm';
import DonatedCamerasList from './pages/third-party-approved-companies/DonatedCameras/DonatedCamerasList';

// Citizen
import CitizenManagement from './pages/citizen/CitizenManagement/CitizenManagement';
import CitizenLprEvents from './pages/citizen/CitizenLprEvents/CitizenLprEvents';
import CitizenLprSearch from './pages/citizen/CitizenLprSearch/CitizenLprSearch';
import CitizenLprEventsDetails from './pages/citizen/CitizenLprEvents/CitizenLprEventsDetails';
import CitizenBlockReasons from './pages/citizen/CitizenBlockReasons/CitizenBlockReasons';

//Param typings
import {
    type ArmoryParamList,
    type ClonedPlateParamList,
    type DashBoardParamList,
    type DeacParamList,
    type LogsParamList,
    type LPRParamList,
    type RootStackParamList,
    type SignaturesParamList,
    type SmartSearchFacialParamList,
    type ThirdPartyApprovedCompaniesParamList,
    CitizenParamList,
    ModuleParamLists,
    FacialParamList,
    EventsParamList,
    WebRtcParamList,
    StreamServersParamList,
    RootScreensParamLists,
} from './typings/Params';

import { DefaultTheme, getStateFromPath, NavigationContainer, NavigationContainerRefWithCurrent, useNavigation } from '@react-navigation/native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import DefaultPageContainer from './components/DefaultPageContainer';
import DefaultPageLayout from './components/DefaultPageLayout';
import { ActivityIndicator, ImageSourcePropType, View } from 'react-native';
import { CurrentApp, useCurrentApp } from './context/CurrentApp';
import zod from 'zod';
import { useStyles } from 'react-native-unistyles';
import { useAuth } from './context/Auth';
import { FacialSchemas } from './schemas/facial';
import { SmartSearchFacialSchemas } from './schemas/smart-search-facial';
import { LprSchemas } from './schemas/lpr';
import { ClonedPlateSchemas } from './schemas/cloned-plate';
import { EventsSchemas } from './schemas/events';
import { LogsSchemas } from './schemas/logs';
import { WebRtcSchemas } from './schemas/webrtc';
import { StreamServersSchemas } from './schemas/stream-servers';
import Publisher from './pages/webrtc/Publisher';
import Consumer from './pages/webrtc/Consumer';
import ServerFarms from './pages/stream-servers/ServerFarms';
import ServerFarmDetail from './pages/stream-servers/ServerFarmDetail';
import { DashboardSchemas } from './schemas/dashboard';
import { SignaturesSchemas } from './schemas/signatures';
import { ArmorySchemas } from './schemas/armory';
import { CitizenSchemas } from './schemas/citizen';
import { ThirdPartyApprovedCompaniesSchemas } from './schemas/third-party-approved-companies';
import { DeacSchemas } from './schemas/deac';
import { RootSchemas } from './schemas/root';
import { dispatchModule } from './routes/dispatch';
import { accessModule } from './routes/access';
import { camerasModule } from './routes/cameras';
import { crmModule } from './routes/crm';
import { documentsModule } from './routes/documents';
import { userModule } from './routes/user';
import { appAgentModule } from './routes/app-agent';
import { guardianAppModule } from './routes/guardian-app';

const Stack = createStackNavigator<RootStackParamList>();

export interface RootNavigation {
    navigate<P extends keyof RootScreensParamLists>(
        page: P,
        params?: RootScreensParamLists[P],
    ): void;
    navigate<
        M extends keyof ModuleParamLists,
        S extends keyof ModuleParamLists[M],
    >(
        module: M,
        options?: {
            screen: S;
            params?: ModuleParamLists[M][S];
        },
    ): void;
    goBack(): void;
    getState(): {
        routeNames: string[];
    };
    addListener(event: string, action: () => void): void;
    setParams(
        params: Record<string, string | number | undefined | boolean | number[] | string[]>,
    ): void;
    canGoBack(): boolean;
}

interface Screens {
    [pageName: string]: {
        path: string;
        screens?: Screens;
        parse?: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            [queryParam: string]: any;
        };
    };
}
export type ModulePage<
    K extends keyof ModuleParamLists,
    S extends keyof ModuleParamLists[K],
> = {
    path: string;
    selectedItem: string;

    initialParams?: () => ModuleParamLists[K][S];

    canAccess: (user: AuthenticatedUser | undefined | null, currentApp: CurrentApp) => boolean;
    /**
     * this will be used on Lateral Menu, Global Search Filter and Browser Tab Title
     */
    label: i18nKeys;
    component: (params: { navigation: RootNavigation; }) => React.JSX.Element;

    /**
     * if you inform this prop, this page will appear on both module Lateral Menu and Global Search Filter
     */
    accessInformation?: {
        icon: IconDefinition | ProIconDefinition;
        iconModifier?: IconDefinition | ProIconDefinition;
        navigate: (nav: RootNavigation) => void;
    };
    ignorePadding?: boolean;
    hideTopMenu?: boolean;
} & ({
    presentation?: 'card';
} | {
    presentation: 'transparentModal';
    parentScreen: keyof ModuleParamLists[K];
});

type ModulePageConfig<K extends keyof ModuleParamLists> = {
    [S in keyof ModuleParamLists[K]]: ModulePage<K, S>;
};

type ReactProvider = React.ComponentType<{ children: React.ReactNode; }>;
export type Module<
    K extends keyof ModuleParamLists,
> = {
    path: string;
    stackNavigator: ReturnType<typeof createStackNavigator<ModuleParamLists[K]>>;
    screens: ModulePageConfig<K>;
    schemas: Record<string, zod.AnyZodObject | zod.ZodOptional<zod.AnyZodObject>>;
    label: i18nKeys;
    canAccess: (
        user: AuthenticatedUser | undefined | null,
        currentApp: CurrentApp,
    ) => boolean;
    navigate: (nav: RootNavigation) => void;
    providers?: ReactProvider | ReactProvider[];
} & ({
    hideFromMenu: true;
} | {
    hideFromMenu?: false;
    logo: ImageSourcePropType;
    icon: ImageSourcePropType;
});

type ModuleConfig = {
    [K in keyof ModuleParamLists]: Module<K>;
};

export type RootScreen = {
    path: string;
    label: i18nKeys;
    canAccess: (
        user: AuthenticatedUser | undefined | null,
        currentApp: CurrentApp,
    ) => boolean;
    component: (params: { navigation: RootNavigation; route: unknown; }) => React.JSX.Element;

    /**
     * if you inform this prop, this page will appear on Global Search Filter
     */
    accessInformation?: {
        icon: IconDefinition | ProIconDefinition;
        iconModifier?: IconDefinition | ProIconDefinition;
        navigate: (nav: RootNavigation) => void;
    };
    selectedItem?: never;
    ignorePadding?: boolean;
    hideTopMenu?: boolean;
} & ({
    presentation?: 'card';
} | {
    presentation: 'transparentModal';
    parentScreen: keyof RootScreensParamLists;
});

type ScreenConfig = {
    [K in keyof RootScreensParamLists]: RootScreen;
};

export interface ApplicationPages {
    screens: ScreenConfig;
    modules: ModuleConfig;
}

interface RouteConfig {
    screens: Screens;
}

function createLinkingParse<T extends zod.ZodRawShape>(
    schema: zod.ZodObject<T> | zod.ZodOptional<zod.ZodObject<T>> | zod.ZodUndefined
) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parse: Record<string, (value: string) => any> = {};

    if (schema instanceof zod.ZodUndefined) {
        return;
    }

    if (schema instanceof zod.ZodOptional) {
        return parse;
    }

    const shape = schema.shape;

    for (const key in shape) {
        if (Object.hasOwn(shape, key)) {
            parse[key] = (value: string) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const result = schema.pick({ [key]: true }).safeParse({
                    [key]: value,
                });

                if (!result.success) {
                    return undefined;
                }

                return result.data[key];
            };
        }
    }

    return parse as {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [K in keyof zod.infer<typeof schema>]: (value: string) => zod.infer<typeof schema>[K];
    };
}


interface DefaultParams {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    route: any;
}

const PagesRoutes = ({ route }: DefaultParams) => {

    const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (route?.params?.pathname) {
                const path = route.params.pathname.split('/');

                if (path.length == 3 && path[1] == 'reset-password') {
                    navigation.navigate('ResetPassword', { token: path[2] });
                }

                if (path.length == 2 && path[1] == 'terms-of-use') {
                    navigation.navigate('TermsOfUse');
                }
            }
            navigation.reset({
                index: 0,
                routes: [{ name: 'Login' }],
            });
        }, 0);

        return () => clearTimeout(timeout);
    }, [navigation, route]);

    return <></>;
};

export const applicationPages: ApplicationPages = {
    screens: {
        MainPage: {
            path: '',
            label: 'SentinelX',
            canAccess: () => true,
            component: PagesRoutes,
        },
        TermsOfUse: {
            path: '/terms-of-use',
            label: 'termsOfUse',
            canAccess: () => true,
            component: TermsOfUse,
            accessInformation: {
                icon: faFileContract,
                navigate: (navigation) => navigation.navigate('TermsOfUse'),
            }
        },
        RotatePassword: {
            path: '/rotate-password',
            label: 'updatePassword',
            canAccess: () => true,
            component: RotatePassword,
            accessInformation: {
                icon: faKey,
                navigate: (navigation) => navigation.navigate('RotatePassword'),
            }
        },
        ChangeLogs: {
            path: '/change-logs',
            label: 'changeLogs',
            canAccess: () => true,
            component: ChangeLogs,
            accessInformation: {
                icon: faCodeCommit,
                navigate: (navigation) => navigation.navigate('ChangeLogs'),
            }
        },
        ContactUs: {
            path: '/contact-us',
            label: 'contactUs',
            canAccess: (user) => Boolean(user?.permissions.create_support_ticket),
            component: ContactUs,
            accessInformation: {
                icon: faPhone,
                navigate: (navigation) => navigation.navigate('ContactUs'),
            }
        },
        FrequentlyAskedQuestions: {
            path: '/frequently-asked-questions',
            label: 'faq',
            canAccess: () => true,
            component: FrequentlyAskedQuestions,
            accessInformation: {
                icon: faCircleQuestion,
                navigate: (navigation) => navigation.navigate('FrequentlyAskedQuestions'),
            }
        },
        PublicFrequentlyAskedQuestions: {
            path: '/public-frequently-asked-questions',
            label: 'faq',
            canAccess: () => true,
            component: PublicFrequentlyAskedQuestions,
            hideTopMenu: true,
        },
        Login: {
            path: '/login',
            label: 'login',
            canAccess: () => true,
            component: Login,
            hideTopMenu: true,
        },
        Menu: {
            path: '/menu',
            label: 'menu',
            canAccess: () => true,
            component: Menu,
        },
        ResetPassword: {
            path: '/reset-password/:token',
            label: 'resetPassword',
            canAccess: () => true,
            component: ResetPassword,
            hideTopMenu: true,
        },
    },
    modules: {
        User: userModule,
        Cameras: camerasModule,
        Facial: {
            stackNavigator: createStackNavigator<FacialParamList>(),
            path: '/facial',
            canAccess: (user, currentApp) => Boolean(user && !currentApp.isAgentApp),
            icon: require('../assets/icon_facial.png'),
            logo: require('../assets/logo_facial.png'),
            navigate: (navigation) => navigation.navigate('Facial'),
            label: 'facial',
            schemas: FacialSchemas,
            screens: {
                LiveEvents: {
                    path: 'live-events',
                    selectedItem: 'live-events',
                    component: LiveEvents,
                    label: 'liveEvents',
                    accessInformation: {
                        icon: faClipboardList,
                        navigate: (navigation) => navigation.navigate('Facial', { screen: 'LiveEvents' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_can_watch_all_events
                        || user?.permissions.facial_events_missing_person
                        || user?.permissions.facial_events_wanted_person
                    )
                },
                LiveEventDetail: {
                    path: 'live-events/event/:eventId',
                    selectedItem: 'live-events',
                    component: LiveEventDetail,
                    label: 'detection',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_can_watch_all_events
                        || user?.permissions.facial_events_missing_person
                        || user?.permissions.facial_events_wanted_person
                    ),
                },
                LiveAlerts: {
                    path: 'live-alerts',
                    selectedItem: 'live-alerts',
                    component: LiveAlerts,
                    label: 'liveAlerts',
                    accessInformation: {
                        icon: faTriangleExclamation,
                        navigate: (navigation) => navigation.navigate('Facial', { screen: 'LiveAlerts' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_can_watch_all_events
                        || user?.permissions.facial_alerts_missing_person
                        || user?.permissions.facial_alerts_wanted_person
                    )
                },
                LiveAlertEventDetail: {
                    path: 'live-alerts/event/:eventId',
                    component: LiveAlertEventDetail,
                    selectedItem: 'live-alerts',
                    label: 'detection',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_can_watch_all_events
                        || user?.permissions.facial_alerts_missing_person
                        || user?.permissions.facial_alerts_wanted_person
                    )
                },
                Search: {
                    path: 'search',
                    selectedItem: 'search',
                    component: Search,
                    label: 'search',
                    initialParams: () => ({
                        onlyWithPerson: true,
                        endIn: moment().toDate().getTime(),
                    }),
                    accessInformation: {
                        icon: faSearch,
                        navigate: (navigation) => navigation.navigate('Facial', { screen: 'Search' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_can_watch_all_events
                        || user?.permissions.facial_alerts_missing_person
                        || user?.permissions.facial_alerts_wanted_person
                        || user?.permissions.facial_events_missing_person
                        || user?.permissions.facial_events_wanted_person
                    ),
                },
                SearchEventDetail: {
                    path: 'search/event/:eventId',
                    selectedItem: 'search',
                    component: SearchEventDetail,
                    label: 'detection',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_can_watch_all_events
                        || user?.permissions.facial_alerts_missing_person
                        || user?.permissions.facial_alerts_wanted_person
                        || user?.permissions.facial_events_missing_person
                        || user?.permissions.facial_events_wanted_person
                    ),
                },
                PeopleList: {
                    path: 'people',
                    selectedItem: 'people',
                    component: PeopleList,
                    label: 'facialRegistration',
                    initialParams: () => ({
                        page: 1,
                        onlyActive: true,
                        limit: 25,
                    }),
                    accessInformation: {
                        icon: faIdBadge,
                        navigate: (navigation) => navigation.navigate('Facial', { screen: 'PeopleList' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.manage_missing_person
                        || user?.permissions.manage_wanted_person
                    ),
                },
                PersonForm: {
                    path: '/people/form/:id?',
                    selectedItem: 'people',
                    component: PersonForm,
                    label: 'registerPerson',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.manage_missing_person
                        || user?.permissions.manage_wanted_person
                    ),
                },
                Markers: {
                    path: 'markers',
                    selectedItem: 'markers',
                    component: Markers,
                    label: 'tags',
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    accessInformation: {
                        icon: faTag,
                        navigate: (navigation) => navigation.navigate('Facial', { screen: 'Markers' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                        || user?.permissions.facial_manage_tags
                    ),
                },
                IgnoredPeople: {
                    path: 'ignored-people',
                    selectedItem: 'ignored-people',
                    component: IgnoredPeople,
                    label: 'ignoredList',
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    accessInformation: {
                        icon: faUserLock,
                        navigate: (navigation) => navigation.navigate('Facial', { screen: 'IgnoredPeople' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.facial_module_access
                    ),
                },
            }
        },
        SmartSearchFacial: {
            path: '/smart-search-facial',
            stackNavigator: createStackNavigator<SmartSearchFacialParamList>(),
            canAccess: (user, currentApp) => Boolean((user?.isAdmin || user?.permissions.smart_search_module_access) && !currentApp.isAgentApp),
            icon: require('../assets/icon_smart_search_facial.png'),
            logo: require('../assets/logo_smart_search_facial.png'),
            navigate: (navigation) => navigation.navigate('SmartSearchFacial'),
            label: 'smartSearchFacial',
            schemas: SmartSearchFacialSchemas,
            screens: {
                SmartSearchFacial: {
                    path: 'search',
                    selectedItem: 'search',
                    canAccess: () => true,
                    component: SmartSearchFacial,
                    label: 'searchByPhoto',
                    accessInformation: {
                        icon: faSearch,
                        navigate: (navigation) => navigation.navigate('SmartSearchFacial', { screen: 'SmartSearchFacial' }),
                    }
                },
            }
        },
        LPR: {
            path: '/LPR',
            stackNavigator: createStackNavigator<LPRParamList>(),
            icon: require('../assets/icon_lpr.png'),
            logo: require('../assets/logo_lpr.png'),
            canAccess: (user, currentApp) => Boolean(user && !currentApp.isAgentApp),
            label: 'lpr',
            navigate: (navigation) => navigation.navigate('LPR'),
            schemas: LprSchemas,
            screens: {
                LprLiveEvents: {
                    path: 'live-events',
                    selectedItem: 'live-events',
                    component: LprLiveEvents,
                    label: 'liveEvents',
                    accessInformation: {
                        icon: faClipboardList,
                        navigate: (navigation) => navigation.navigate('LPR', { screen: 'LprLiveEvents' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_can_watch_all_events
                    )
                },
                LiveEventLprDetection: {
                    path: 'live-events/lpr-detection/:detectionId',
                    selectedItem: 'live-events',
                    component: LiveEventLprDetection,
                    label: 'detection',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_can_watch_all_events
                    )
                },
                LprLiveAlerts: {
                    path: 'live-alerts',
                    selectedItem: 'live-alerts',
                    component: LprLiveAlerts,
                    label: 'liveAlerts',
                    accessInformation: {
                        icon: faTriangleExclamation,
                        navigate: (navigation) => navigation.navigate('LPR', { screen: 'LprLiveAlerts' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_can_watch_all_events
                    )
                },
                LiveAlertLprDetection: {
                    path: 'live-alerts/lpr-detection/:detectionId',
                    selectedItem: 'live-alerts',
                    component: LiveAlertLprDetection,
                    label: 'detection',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_can_watch_all_events
                    )
                },
                LprSearch: {
                    path: 'search',
                    selectedItem: 'search',
                    component: LprSearch,
                    label: 'search',
                    initialParams: () => ({
                        endIn: moment().toDate().getTime(),
                        irregularSituation: false,
                        isMotorcycle: false,
                    }),
                    accessInformation: {
                        icon: faSearch,
                        navigate: (navigation) => navigation.navigate('LPR', { screen: 'LprSearch' }),
                    },
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_can_watch_all_events
                    )
                },
                SearchLprDetection: {
                    path: 'search/lpr-detection/:detectionId',
                    selectedItem: 'search',
                    component: SearchLprDetection,
                    label: 'detection',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_can_watch_all_events
                    )
                },
                Plates: {
                    path: 'plates',
                    selectedItem: 'plates',
                    component: Plates,
                    label: 'plates',
                    accessInformation: {
                        icon: faCar,
                        navigate: (navigation) => navigation.navigate('LPR', { screen: 'Plates' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                        onlyActive: true,
                    }),
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                        || user?.permissions.lpr_edit_denylist
                        || user?.permissions.lpr_see_denylist
                        || user?.permissions.lpr_edit_illegal_debris
                        || user?.permissions.lpr_see_illegal_debris
                    )

                },
                LprReports: {
                    path: 'reports',
                    selectedItem: 'reports',
                    component: LprReports,
                    label: 'reports',
                    accessInformation: {
                        icon: faFolderOpen,
                        navigate: (navigation) => navigation.navigate('LPR', { screen: 'LprReports' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.lpr_module_access
                    )
                },
            }
        },
        ClonedPlate: {
            path: '/cloned-plate',
            stackNavigator: createStackNavigator<ClonedPlateParamList>(),
            icon: require('../assets/icon_cloned_plate.png'),
            logo: require('../assets/logo_cloned_plate.png'),
            canAccess: (user, currentApp) => Boolean(user && !currentApp.isAgentApp),
            label: 'clonedPlate',
            navigate: (navigation) => navigation.navigate('ClonedPlate'),
            schemas: ClonedPlateSchemas,
            screens: {
                ClonedPlates: {
                    path: 'cloned-plates',
                    selectedItem: 'cloned-plates',
                    component: ClonedPlates,
                    label: 'clonedPlates',
                    accessInformation: {
                        icon: faCar,
                        navigate: (navigation) => navigation.navigate('ClonedPlate', { screen: 'ClonedPlates' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.cloned_plate_module_access
                    )
                },
                CloneSuspicions: {
                    path: 'clone-suspicions',
                    selectedItem: 'clone-suspicions',
                    component: CloneSuspicions,
                    label: 'cloneSuspicions',
                    accessInformation: {
                        icon: faClipboard,
                        navigate: (navigation) => navigation.navigate('ClonedPlate', { screen: 'CloneSuspicions' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.cloned_plate_module_access
                    )
                },
            }
        },
        Events: {
            path: '/events',
            stackNavigator: createStackNavigator<EventsParamList>(),
            canAccess: (user, currentApp) => Boolean(!currentApp.isAgentApp && (
                user?.isAdmin ||
                user?.permissions.events_module_access ||
                user?.permissions.events_manage_missing_face_occurrence ||
                user?.permissions.events_manage_not_allowed__face_occurrence ||
                user?.permissions.events_manage_wanted_face_occurrence ||
                user?.permissions.events_manage_wanted_plate_occurrence ||
                user?.permissions.events_manage_wanted_plate_by_mobile_agent_occurrence ||
                user?.permissions.events_manage_irregular_vehicle_occurrence ||
                user?.permissions.events_manage_clone_suspect_occurrence ||
                user?.permissions.events_manage_illegal_debris_occurrence ||
                user?.permissions.events_manage_area_invasion_occurrence ||
                user?.permissions.events_manage_people_crowd_occurrence ||
                user?.permissions.events_manage_mp_wanted_plate_occurrence ||
                user?.permissions.events_manage_mp_wanted_mobile_agent_plate_occurrence ||
                user?.permissions.events_manage_mp_wanted_face_occurrence ||
                user?.permissions.events_manage_alarm_center_occurrence ||
                user?.permissions.events_manage_guardian_app_occurrence ||
                user?.permissions.events_manage_school_app_occurrence ||
                user?.permissions.events_manage_sigrc_occurrence ||
                user?.permissions.events_manage_cortex_plate_occurrence ||
                user?.permissions.events_manage_manual_occurrence ||
                user?.permissions.events_manage_camera_depredation_occurrence
            )),
            icon: require('../assets/icon_events.png'),
            logo: require('../assets/logo_events.png'),
            label: 'events',
            navigate: (navigation) => navigation.navigate('Events'),
            schemas: EventsSchemas,
            screens: {
                EventsBoard: {
                    path: 'events',
                    selectedItem: 'events',
                    component: EventsBoard,
                    label: 'events',
                    initialParams: () => ({
                        beginIn: moment().subtract(12, 'hours').valueOf(),
                    }),
                    accessInformation: {
                        icon: faIdBadge,
                        navigate: (navigation) => navigation.navigate('Events', { screen: 'EventsBoard' }),
                    },
                    canAccess: () => true,
                },
                EventsOccurrenceDetail: {
                    path: 'events/detail/:occurrenceId',
                    selectedItem: 'events',
                    component: EventsOccurrenceDetail,
                    label: 'occurrences',
                    canAccess: () => true,
                },
            }
        },
        Dispatch: dispatchModule,
        AppAgent: appAgentModule,
        GuardianApp: guardianAppModule,
        Access: accessModule,
        CRM: crmModule,
        Logs: {
            path: '/logs',
            stackNavigator: createStackNavigator<LogsParamList>(),
            canAccess: (user, currentApp) => Boolean(
                !currentApp.isAgentApp && (
                    user?.isAdmin
                    || user?.permissions.logs_module_access
                )
            ),
            icon: require('../assets/icon_logs.png'),
            logo: require('../assets/logo_logs.png'),
            label: 'logs',
            navigate: (navigation) => navigation.navigate('Logs'),
            schemas: LogsSchemas,
            screens: {
                LogList: {
                    path: 'search',
                    selectedItem: 'search',
                    component: LogList,
                    label: 'logRegistries',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faFileContract,
                        navigate: (navigation) => navigation.navigate('Logs', { screen: 'LogList' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                        dateLte: moment().endOf('day').valueOf(),
                        dateGte: moment().startOf('day').valueOf(),
                        sort: 'desc',
                    }),
                },
                LogReports: {
                    path: 'reports',
                    selectedItem: 'reports',
                    component: LogReports,
                    label: 'reports',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faFolderOpen,
                        navigate: (navigation) => navigation.navigate('Logs', { screen: 'LogReports' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
            }
        },
        Dashboard: {
            path: '/dashboard',
            stackNavigator: createStackNavigator<DashBoardParamList>(),
            canAccess: (user, currentApp) => Boolean(
                user && !currentApp.isAgentApp
            ),
            icon: require('../assets/icon_dashboard.png'),
            logo: require('../assets/logo_dashboard.png'),
            label: 'dashboard',
            navigate: (navigation) => navigation.navigate('Dashboard'),
            schemas: DashboardSchemas,
            screens: {
                DashboardOccurrences: {
                    selectedItem: 'occurrences',
                    path: 'occurrences',
                    component: DashboardOccurrences,
                    label: 'occurrences',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.dashboard_module_access
                        || user?.permissions.dashboard_see_occurrence
                    ),
                    accessInformation: {
                        icon: faClipboardList,
                        navigate: (navigation) => navigation.navigate('Dashboard', { screen: 'DashboardOccurrences' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                        beginIn: moment().subtract(12, 'hours').valueOf(),
                    }),
                },
                DashboardPlateMonitoring: {
                    selectedItem: 'plate-monitoring',
                    path: 'plate-monitoring',
                    component: DashboardPlateMonitoring,
                    label: 'plateMonitoring',
                    canAccess: (user) => Boolean(
                        user?.permissions.dashboard_module_access
                        || user?.permissions.dashboard_manage_monitoring_plates_groups
                        || user?.permissions.dashboard_manage_monitoring_plates
                        || user?.permissions.dashboard_see_monitoring_plates
                    ),
                    accessInformation: {
                        icon: faCarBus,
                        navigate: (navigation) => navigation.navigate('Dashboard', { screen: 'DashboardPlateMonitoring' }),
                    },
                },
                DashboardCameras: {
                    selectedItem: 'cameras',
                    path: 'cameras',
                    component: DashboardCameras,
                    label: 'cameras',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.dashboard_module_access
                        || user?.permissions.dashboard_see_cameras
                    ),
                    accessInformation: {
                        icon: faCameraCctv,
                        navigate: (navigation) => navigation.navigate('Dashboard', { screen: 'DashboardCameras' }),
                    },
                },
                DashboardAgents: {
                    selectedItem: 'agents',
                    path: 'agents',
                    component: DashboardAgents,
                    label: 'agents',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.dashboard_module_access
                        || user?.permissions.dashboard_see_agents
                        || user?.permissions.dashboard_see_agents_acting_bodies
                        || user?.permissions.dashboard_see_agents_acting_body_units
                    ),
                    accessInformation: {
                        icon: faCameraCctv,
                        navigate: (navigation) => navigation.navigate('Dashboard', { screen: 'DashboardAgents' }),
                    },
                },
                DashboardVehicles: {
                    selectedItem: 'vehicles',
                    path: 'vehicles',
                    component: DashboardVehicles,
                    label: 'vehicles',
                    canAccess: (user) => Boolean(
                        user?.isAdmin
                        || user?.permissions.dashboard_see_vehicles
                        || user?.permissions.dashboard_see_vehicles_acting_bodies
                        || user?.permissions.dashboard_see_vehicles_acting_body_units
                    ),
                    accessInformation: {
                        icon: faCar,
                        navigate: (navigation) => navigation.navigate('Dashboard', { screen: 'DashboardVehicles' }),
                    },
                },
            }
        },
        Signatures: {
            path: '/signatures',
            stackNavigator: createStackNavigator<SignaturesParamList>(),
            canAccess: (user, currentApp) => Boolean(
                !currentApp.isAgentApp && (
                    user?.isAdmin ||
                    user?.permissions.supervisor_sign_report ||
                    user?.permissions.supervisor_sign_report_unit
                )
            ),
            icon: require('../assets/icon_signatures.png'),
            logo: require('../assets/logo_signatures.png'),
            label: 'signatures',
            navigate: (navigation) => navigation.navigate('Signatures'),
            schemas: SignaturesSchemas,
            screens: {
                Signatures: {
                    path: 'signatures',
                    selectedItem: 'signatures',
                    component: Signatures,
                    label: 'signatures',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faFileSignature,
                        navigate: (navigation) => navigation.navigate('Signatures', { screen: 'Signatures' }),
                    },
                    initialParams: () => ({
                        limit: 25,
                        page: 1,
                    }),
                }
            },
        },
        Documents: documentsModule,
        Armory: {
            path: '/armory',
            stackNavigator: createStackNavigator<ArmoryParamList>(),
            canAccess: (user, currentApp) => Boolean(
                user && !currentApp.isAgentApp
            ),
            icon: require('../assets/icon_armory.png'),
            logo: require('../assets/logo_armory.png'),
            label: 'armory',
            navigate: (navigation) => navigation.navigate('Armory'),
            schemas: ArmorySchemas,
            screens: {
                ArmoryShifts: {
                    path: 'armory-shifts',
                    selectedItem: 'armory-shifts',
                    component: ArmoryShifts,
                    label: 'manageShifts',
                    canAccess: (user) => Boolean(
                        user?.permissions.armory_manage_armory_shift ||
                        user?.permissions.armory_manage_acting_body_units_armory_shift ||
                        user?.permissions.armory_manage_all_acting_bodies_armory_shift ||
                        user?.permissions.armory_replace_in_charge_armourer
                    ),
                    accessInformation: {
                        icon: faClock,
                        navigate: (navigation) => navigation.navigate('Armory', { screen: 'ArmoryShifts' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
                ArmoryShiftForm: {
                    path: 'armory-shifts/form/:id?',
                    selectedItem: 'armory-shifts',
                    component: ArmoryShiftForm,
                    label: 'manageShifts',
                    canAccess: (user) => Boolean(
                        user?.permissions.armory_manage_armory_shift ||
                        user?.permissions.armory_manage_acting_body_units_armory_shift ||
                        user?.permissions.armory_manage_all_acting_bodies_armory_shift ||
                        user?.permissions.armory_replace_in_charge_armourer
                    ),
                },
                MyArmoryShifts: {
                    path: 'my-armory-shifts',
                    selectedItem: 'my-armory-shifts',
                    component: MyArmoryShifts,
                    label: 'myShifts',
                    canAccess: (user) => Boolean(
                        user?.permissions.armory_assigned_as_armourer
                    ),
                    accessInformation: {
                        icon: faClipboardListCheck,
                        navigate: (navigation) => navigation.navigate('Armory', { screen: 'MyArmoryShifts' }),
                    },
                }
            },
        },
        Deac: {
            path: '/deac',
            stackNavigator: createStackNavigator<DeacParamList>(),
            canAccess: (user, currentApp) => Boolean(
                user && !currentApp.isAgentApp
            ),
            icon: require('../assets/icon_deac.png'),
            logo: require('../assets/logo_deac.png'),
            label: 'deac',
            navigate: (navigation) => navigation.navigate('Deac'),
            schemas: DeacSchemas,
            screens: {
                Dashboard: {
                    path: 'dashboard',
                    selectedItem: 'dashboard',
                    component: Dashboard,
                    label: 'dashboard',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_dashboard ||
                        user?.permissions.deac_manage_acting_body_units_dashboard ||
                        user?.permissions.deac_manage_all_acting_bodies_dashboard
                    ),
                    accessInformation: {
                        icon: faChartPieSimple,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'Dashboard' }),
                    },
                    initialParams: () => ({
                        beginIn: moment().startOf('day').subtract(30, 'days').valueOf(),
                        endIn: moment().endOf('day').valueOf(),
                    })
                },
                Vacancies: {
                    path: 'vacancies',
                    selectedItem: 'vacancies',
                    component: Vacancies,
                    label: 'vacanciesManagement',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_acting_body_units_vacancies ||
                        user?.permissions.deac_manage_all_acting_bodies_vacancies ||
                        user?.permissions.deac_manage_vacancies ||
                        user?.permissions.deac_visualize_vacancies
                    ),
                    accessInformation: {
                        icon: faFilePen,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'Vacancies' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                VacancyForm: {
                    path: 'vacancies-form/:id?',
                    selectedItem: 'vacancies',
                    component: VacancyForm,
                    label: 'vacanciesManagement',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_acting_body_units_vacancies ||
                        user?.permissions.deac_manage_all_acting_bodies_vacancies ||
                        user?.permissions.deac_manage_vacancies ||
                        user?.permissions.deac_visualize_vacancies
                    ),
                },
                Applications: {
                    path: 'applications',
                    selectedItem: 'applications',
                    component: Applications,
                    label: 'candidacies',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_applications ||
                        user?.permissions.deac_manage_acting_body_units_applications ||
                        user?.permissions.deac_manage_all_acting_bodies_applications
                    ),
                    accessInformation: {
                        icon: faMemoCircleCheck,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'Applications' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                VacanciesTemplates: {
                    path: 'vacancies-templates',
                    selectedItem: 'vacancies-templates',
                    component: VacanciesTemplates,
                    label: 'vacanciesTemplates',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_acting_body_units_vacancies_templates ||
                        user?.permissions.deac_manage_all_acting_bodies_vacancies_templates ||
                        user?.permissions.deac_manage_vacancies_templates
                    ),
                    accessInformation: {
                        icon: faHexagonNodesBolt,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'VacanciesTemplates' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                VacancyTemplateForm: {
                    path: 'vacancies-templates-form/:id?',
                    selectedItem: 'vacancies-templates',
                    component: VacancyTemplateForm,
                    label: 'vacanciesTemplates',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_acting_body_units_vacancies_templates ||
                        user?.permissions.deac_manage_all_acting_bodies_vacancies_templates ||
                        user?.permissions.deac_manage_vacancies_templates
                    ),
                },
                PresenceControl: {
                    path: 'presence-control',
                    selectedItem: 'presence-control',
                    component: PresenceControl,
                    label: 'presenceControl',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_presence_control ||
                        user?.permissions.deac_manage_acting_body_units_presence_control ||
                        user?.permissions.deac_manage_all_acting_bodies_presence_control
                    ),
                    accessInformation: {
                        icon: faMemoCircleCheck,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'PresenceControl' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                VacancyLevels: {
                    path: 'vacancy-levels',
                    selectedItem: 'vacancy-levels',
                    component: VacancyLevels,
                    label: 'vacancyLevels',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_acting_body_vacancy_level ||
                        user?.permissions.deac_manage_all_acting_bodies_vacancy_level
                    ),
                    accessInformation: {
                        icon: faSitemap,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'VacancyLevels' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                Workplaces: {
                    path: 'workplaces',
                    selectedItem: 'workplaces',
                    component: Workplaces,
                    label: 'workplaces',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_workplaces ||
                        user?.permissions.deac_manage_acting_body_units_workplaces ||
                        user?.permissions.deac_manage_all_acting_bodies_workplaces
                    ),
                    accessInformation: {
                        icon: faBuildingShield,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'Workplaces' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                VacancyTags: {
                    path: 'vacancy-tags',
                    selectedItem: 'vacancy-tags',
                    component: VacancyTags,
                    label: 'vacancyTags',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_vacancy_tags
                    ),
                    accessInformation: {
                        icon: faTag,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'VacancyTags' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                VacancyCancellationReasons: {
                    path: 'vacancy-cancellation-reasons',
                    selectedItem: 'vacancy-cancellation-reasons',
                    component: VacancyCancellationReasons,
                    label: 'vacancyCancellationReasons',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_vacancy_cancellation_reasons
                    ),
                    accessInformation: {
                        icon: faBan,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'VacancyCancellationReasons' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
                DeacReports: {
                    path: 'reports',
                    selectedItem: 'reports',
                    component: DeacReports,
                    label: 'reports',
                    canAccess: (user) => Boolean(
                        user?.permissions.deac_manage_acting_body_units_reports ||
                        user?.permissions.deac_manage_all_acting_bodies_reports
                    ),
                    accessInformation: {
                        icon: faFolderOpen,
                        navigate: (navigation) => navigation.navigate('Deac', { screen: 'DeacReports' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    })
                },
            }
        },
        ThirdPartyApprovedCompanies: {
            path: '/third-party-approved-companies',
            stackNavigator: createStackNavigator<ThirdPartyApprovedCompaniesParamList>(),
            canAccess: (user, currentApp) => Boolean(
                user && !currentApp.isAgentApp
            ),
            icon: require('../assets/icon_approved_companies.png'),
            logo: require('../assets/logo_approved_companies.png'),
            label: 'approvedCompanies',
            navigate: (navigation) => navigation.navigate('ThirdPartyApprovedCompanies'),
            schemas: ThirdPartyApprovedCompaniesSchemas,
            screens: {
                InscriptionsManagement: {
                    path: 'inscriptions-management',
                    selectedItem: 'inscriptions-management',
                    component: Inscriptions,
                    label: 'inscriptionsManagement',
                    canAccess: (user) => Boolean(
                        user?.permissions.third_party_approved_companies_manage_inscriptions
                    ),
                    accessInformation: {
                        icon: faFilePen,
                        navigate: (navigation) => navigation.navigate('ThirdPartyApprovedCompanies', { screen: 'InscriptionsManagement' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
                InscriptionForm: {
                    path: 'inscription-form/:id?',
                    selectedItem: 'inscriptions-management',
                    component: InscriptionForm,
                    label: 'inscriptionsManagement',
                    canAccess: (user) => Boolean(
                        user?.permissions.third_party_approved_companies_manage_inscriptions
                    ),
                },
                DonatedCamerasList: {
                    path: 'donated-cameras',
                    selectedItem: 'donated-cameras',
                    component: DonatedCamerasList,
                    label: 'donatedCameras',
                    canAccess: (user) => Boolean(
                        user?.permissions.manage_third_party_donated_cameras
                    ),
                    accessInformation: {
                        icon: faHandHoldingBox,
                        navigate: (navigation) => navigation.navigate('ThirdPartyApprovedCompanies', { screen: 'DonatedCamerasList' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
            }
        },
        Citizen: {
            path: '/citizen',
            stackNavigator: createStackNavigator<CitizenParamList>(),
            canAccess: (user, currentApp) => Boolean(
                user && !currentApp.isAgentApp
            ),
            icon: require('../assets/icon_sentinel_app.png'),
            logo: require('../assets/logo_citizen_app.png'),
            label: 'citizenApp',
            navigate: (navigation) => navigation.navigate('Citizen'),
            schemas: CitizenSchemas,
            screens: {
                CitizenManagement: {
                    path: 'citizen-management',
                    selectedItem: 'citizen-management',
                    component: CitizenManagement,
                    label: 'citizenManagement',
                    canAccess: (user) => Boolean(
                        user?.permissions.citizen_management
                    ),
                    accessInformation: {
                        icon: faUser,
                        navigate: (navigation) => navigation.navigate('Citizen', { screen: 'CitizenManagement' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
                CitizenLprSearch: {
                    path: 'citizen-lpr-search',
                    selectedItem: 'citizen-lpr-search',
                    component: CitizenLprSearch,
                    label: 'plateSearch',
                    canAccess: (user) => Boolean(
                        user?.permissions.citizen_watch_lpr_events
                    ),
                    accessInformation: {
                        icon: faMagnifyingGlass,
                        navigate: (navigation) => navigation.navigate('Citizen', { screen: 'CitizenLprSearch' }),
                    },
                    initialParams: () => ({
                        endIn: moment().valueOf(),
                    }),
                },
                CitizenLprEvents: {
                    path: 'citizen-lpr-events',
                    selectedItem: 'citizen-lpr-events',
                    component: CitizenLprEvents,
                    label: 'citizenLprEvents',
                    canAccess: (user) => Boolean(
                        user?.permissions.citizen_watch_lpr_events
                    ),
                    accessInformation: {
                        icon: faCar,
                        navigate: (navigation) => navigation.navigate('Citizen', { screen: 'CitizenLprEvents' }),
                    },
                },
                CitizenLprEventsDetails: {
                    path: 'citizen-lpr-events/details/:id',
                    selectedItem: 'citizen-lpr-events',
                    component: CitizenLprEventsDetails,
                    label: 'citizenLprEvents',
                    canAccess: (user) => Boolean(
                        user?.permissions.citizen_watch_lpr_events
                    ),
                },
                CitizenBlockReasons: {
                    path: 'citizen-block-reasons',
                    selectedItem: 'citizen-block-reasons',
                    component: CitizenBlockReasons,
                    label: 'citizenBlockReasons',
                    canAccess: (user) => Boolean(
                        user?.permissions.citizen_management_banishment
                    ),
                    accessInformation: {
                        icon: faBan,
                        navigate: (navigation) => navigation.navigate('Citizen', { screen: 'CitizenBlockReasons' }),
                    },
                    initialParams: () => ({
                        page: 1,
                        limit: 25,
                    }),
                },
            }
        },
        WebRTC: {
            path: '/webrtc',
            stackNavigator: createStackNavigator<WebRtcParamList>(),
            canAccess: (user, currentApp) => Boolean(user?.isAdmin && currentApp.isParentApp),
            label: 'webrtc',
            icon: require('../assets/icon_webrtc.png'),
            logo: require('../assets/logo_webrtc.png'),
            navigate: (navigation) => navigation.navigate('WebRTC'),
            schemas: WebRtcSchemas,
            screens: {
                Publisher: {
                    path: 'publisher',
                    selectedItem: 'publisher',
                    component: Publisher,
                    label: 'publisher',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faCloudArrowUp,
                        navigate: (navigation) => navigation.navigate('WebRTC', { screen: 'Publisher' }),
                    },
                },
                Consumer: {
                    path: 'consumer',
                    selectedItem: 'consumer',
                    component: Consumer,
                    label: 'consumer',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faCloudArrowDown,
                        navigate: (navigation) => navigation.navigate('WebRTC', { screen: 'Consumer' }),
                    },
                },
            }
        },
        StreamServers: {
            path: '/stream-servers',
            stackNavigator: createStackNavigator<StreamServersParamList>(),
            canAccess: (user, currentApp) => Boolean(user?.isAdmin && currentApp.isParentApp),
            label: 'streamServers',
            icon: require('../assets/icon_stream_servers.png'),
            logo: require('../assets/logo_stream_servers.png'),
            navigate: (navigation) => navigation.navigate('StreamServers'),
            schemas: StreamServersSchemas,
            screens: {
                ServerFarms: {
                    path: 'server-farms',
                    selectedItem: 'server-farms',
                    component: ServerFarms,
                    label: 'serverFarms',
                    canAccess: () => true,
                    accessInformation: {
                        icon: faServer,
                        navigate: (navigation) => navigation.navigate('StreamServers', { screen: 'ServerFarms' }),
                    },
                },
                ServerFarmDetail: {
                    path: 'server-farm/:id',
                    selectedItem: 'server-farms',
                    component: ServerFarmDetail,
                    label: 'serverFarmDetail',
                    canAccess: () => true,
                }
            }
        },
    },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const modulePages: { [screen: string]: { modulePath?: string; screenObj: ModulePage<any, any> | RootScreen; }; } = {};

const config: RouteConfig = { screens: {} };

for (const [screen, screenObj] of Object.entries(applicationPages.screens)) {

    const schema = RootSchemas[screen as keyof RootScreensParamLists];

    config.screens[screen] = {
        path: screenObj.path,
        parse: schema ? createLinkingParse(schema) : undefined,
    };

    modulePages[screen] = {
        screenObj: screenObj
    };
}

for (const [screen, screenObj] of Object.entries(applicationPages.modules)) {
    config.screens[screen] = {
        path: screenObj.path,
        screens: {},
    };

    for (const [moduleScreen, moduleScreenObj] of Object.entries(screenObj.screens)) {
        modulePages[moduleScreen] = {
            modulePath: screenObj.path,
            screenObj: moduleScreenObj
        };

        const schema = screenObj.schemas[moduleScreen];

        config.screens[screen].screens![moduleScreen] = {
            path: moduleScreenObj.path,
            parse: createLinkingParse(schema),
        };
    }
}

export const linking = {
    prefixes: ['https://sentinelx.com.br'],
    config,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getStateFromPath(path: string, options: any) {
        const state = getStateFromPath(path, options);

        if (!state) {
            return state;
        }

        const modulePage = state.routes[0]?.state?.routes[0];

        if (modulePage) {
            const screenObj = modulePages[modulePage.name].screenObj;

            if (screenObj.presentation === 'transparentModal') {
                return {
                    ...state,
                    routes: [{
                        ...state.routes[0],
                        state: {
                            index: 1,
                            routes: [{
                                name: screenObj.parentScreen as string,
                            }, {
                                name: modulePage.name,
                                params: modulePage.params,
                            }],
                        },
                    }],
                };
            }
        }
        return state;
    }
};

export function Loader() {
    const { theme } = useStyles();

    return <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size='large' color={theme.colors.loadingColor} />
    </View>;
}

export function MySafeAreaProvider(props: {
    children: React.ReactNode | undefined;
    navigationRef: NavigationContainerRefWithCurrent<RootStackParamList>;
}) {
    const { theme } = useStyles();
    const [, updateState] = React.useState({});
    const forceUpdate = React.useCallback(() => updateState({}), []);

    props.navigationRef.addListener('state', () => {
        forceUpdate();
    });

    const pathname = useMemo(() => window.location.pathname, [window.location.pathname]);

    return <SafeAreaProvider
        style={{
            backgroundColor: theme.colors.defaultPageContainer.background
        }}
    >
        <MenuProvider>
            <NavigationContainer ref={props.navigationRef} linking={linking} theme={{
                ...DefaultTheme,
                colors: {
                    ...DefaultTheme.colors,
                    background: theme.colors.defaultPageContainer.background
                }
            }} >
                {pathname.includes('/reset-password/') || pathname.includes('/login') ?
                    props.children
                    : <DefaultPageContainer >
                        <DefaultPageLayout navigationRef={props.navigationRef}>
                            {props.children}
                        </DefaultPageLayout>
                    </DefaultPageContainer>
                }
            </NavigationContainer>
        </MenuProvider>
    </SafeAreaProvider>;
}

function renderModule<K extends keyof ModuleParamLists>(
    module: ModuleConfig[K],
    authenticatedUser: AuthenticatedUser | null | undefined,
    currentApp: CurrentApp,
) {
    const { Navigator, Screen } = module.stackNavigator;

    return (
        <Navigator>
            {Object.entries(module.screens).map(([screenName, screen]) => {

                if (!(screen as ModulePage<keyof ModuleParamLists, never>).canAccess(authenticatedUser, currentApp)) {
                    return null;
                }

                return (
                    <Screen
                        key={screenName as string}
                        name={screenName as keyof ModuleParamLists[K]}
                        component={(screen as ModulePage<keyof ModuleParamLists, never>).component}
                        initialParams={(screen as ModulePage<keyof ModuleParamLists, never>).initialParams?.()}
                        options={{
                            headerShown: false,
                            title: translate((screen as ModulePage<keyof ModuleParamLists, never>).label),
                            presentation: (screen as ModulePage<keyof ModuleParamLists, never>).presentation,
                        }}
                    />
                );
            })}
        </Navigator>
    );
}

type RouteEntry = {
    path: string;
};

export const routeMap = (() => {
    const map = new Map<string, RouteEntry>();

    // Root screens
    for (const [screenName, screen] of Object.entries(applicationPages.screens)) {
        map.set(screenName, {
            path: screen.path,
        });
    }

    // Module screens
    for (const module of Object.values(applicationPages.modules)) {
        for (const [screenName, screen] of Object.entries(module.screens)) {
            map.set(screenName, {
                path: join(module.path, screen.path),
            });
        }
    }

    return map;
})();

function join(a: string, b: string) {
    return `${a.replace(/\/$/, '')}/${b.replace(/^\//, '')}`;
}

function composeProviders(
    content: React.ReactNode,
    providers?: ReactProvider | ReactProvider[]
): React.ReactNode {
    if (!providers) {
        return content;
    }

    const providerList = Array.isArray(providers) ? providers : [providers];

    // reduceRight applies the function from the last element to the first in the array:
    // Exemple for [ProviderA, ProviderB]:
    // 1º execution: <ProviderB>{content}</ProviderB>
    // 2º execution: <ProviderA><ProviderB>{content}</ProviderB></ProviderA>
    return providerList.reduceRight((acc, Provider) => {
        return <Provider>{acc}</Provider>;
    }, content);
}

export default function MainRoutes() {

    const { user: authenticatedUser } = useAuth();
    const currentApp = useCurrentApp();

    return (
        <Stack.Navigator>

            {Object.entries(applicationPages.screens).map(([screenName, screenObj]) => {
                if (!screenObj.canAccess(authenticatedUser, currentApp)) {
                    null;
                }
                return <Stack.Screen key={screenName} name={screenName as keyof RootScreensParamLists} component={screenObj.component} options={{
                    headerShown: false,
                    title: translate(screenObj.label),
                }} />;
            })}

            {Object.entries(applicationPages.modules).map(([moduleName, moduleObj]) => {
                const canAccessModule = moduleObj.canAccess(authenticatedUser, currentApp) &&
                    Object.values(
                        moduleObj.screens,
                    ).some(screen => screen.canAccess(authenticatedUser, currentApp));

                if (!canAccessModule) {
                    return null;
                }

                return <Stack.Screen key={moduleName} name={moduleName as keyof ModuleParamLists} options={{ headerShown: false }}>
                    {() => {
                        const moduleContent = renderModule(moduleObj, authenticatedUser, currentApp);
                        return composeProviders(moduleContent, moduleObj.providers);
                    }}
                </Stack.Screen>;
            })}
        </Stack.Navigator >
    );
}
